import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Tab, Tabs } from 'react-bootstrap';
import Groq from 'groq-sdk';

import GeneralSettings from './GeneralSettings';
import AdvancedSettings from './AdvancedSettings';
import ReplaceSettings from './ReplaceSettings';
import ConfirmationModal from './ConfirmationModal';
import ToastMessage from './ToastMessage';

import { DEFAULT_SETTINGS, SETTINGS_KEY, fallbackModels } from '../../utils/settingsDefaults';

function SettingsModal({ show, handleClose, onSave }) {
  const [tabKey, setTabKey] = useState('general');

  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [maxLength, setMaxLength] = useState('');
  const [temperature, setTemperature] = useState('');
  const [topP, setTopP] = useState('');
  const [presencePenalty, setPresencePenalty] = useState('');
  const [frequencyPenalty, setFrequencyPenalty] = useState('');
  const [modelsList, setModelsList] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [apiKeyChanged, setApiKeyChanged] = useState(false);
  const [replacements, setReplacements] = useState([]);

  const [toastShow, setToastShow] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [toastVariant, setToastVariant] = useState('light');

  const [confirmDeleteShow, setConfirmDeleteShow] = useState(false);

  const prevApiKey = useRef('');

  const showToast = (content, variant = 'light') => {
    setToastContent(content);
    setToastVariant(variant);
    setToastShow(true);
  };

  const fetchModels = async (key) => {
    setLoadingModels(true);
    try {
      const groq = new Groq({ apiKey: key, dangerouslyAllowBrowser: true });
      const res = await groq.models.list();
      const models = res.data.map((m) => m.id);

      if (models.length > 0) {
        setModelsList(models);

        // چک کن مدل ذخیره شده هنوز در لیست هست یا نه
        if (model && !models.includes(model)) {
          showToast('⚠️ مدل انتخاب شده قبلا وجود داشت اما الان در لیست موجود نیست.', 'warning');
          setModel(models[0]); // اولین مدل رو اتوماتیک انتخاب کن
        }
      } else {
        setModelsList(fallbackModels);
        showToast('⚠️ مدل‌های فعال پیدا نشدند. از لیست پیشفرض استفاده شد.', 'warning');
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      setModelsList(fallbackModels);
      showToast('❌ دریافت لیست مدل‌ها ناموفق بود. از fallback استفاده شد.', 'danger');
    } finally {
      setLoadingModels(false);
    }
  };

  useEffect(() => {
    if (show) {
      const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
      setApiKey(saved.apiKey || '');
      setModel(saved.model || DEFAULT_SETTINGS.model);
      setMaxLength(saved.maxLength || DEFAULT_SETTINGS.maxLength);
      setTemperature(saved.temperature || DEFAULT_SETTINGS.temperature);
      setTopP(saved.topP || DEFAULT_SETTINGS.topP);
      setPresencePenalty(saved.presencePenalty || DEFAULT_SETTINGS.presencePenalty);
      setFrequencyPenalty(saved.frequencyPenalty || DEFAULT_SETTINGS.frequencyPenalty);
      setReplacements(saved.replacements || []);
      prevApiKey.current = saved.apiKey || '';

      if (saved.apiKey && saved.apiKey.length > 20) {
        fetchModels(saved.apiKey);
      }
    }
  }, [show]);

  useEffect(() => {
    if (apiKeyChanged && apiKey.length > 20) {
      fetchModels(apiKey);
      setApiKeyChanged(false);
    }
  }, [apiKeyChanged, apiKey]);

  const handleApiKeyChange = (newKey) => {
    if (newKey !== prevApiKey.current) {
      setApiKeyChanged(true);
    }
  };

  const handleSave = () => {
    const settings = {
      apiKey,
      model,
      maxLength,
      temperature,
      topP,
      presencePenalty,
      frequencyPenalty,
      replacements,
    };

    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
      prevApiKey.current = apiKey;
      onSave(settings);

      showToast('✅ تنظیمات با موفقیت ذخیره شد.', 'success');
      handleClose();
    } catch (error) {
      console.error('Save settings error:', error);
      showToast('❌ خطا در ذخیره تنظیمات.', 'danger');
    }
  };

  const confirmClearSettings = () => {
    setConfirmDeleteShow(true);
  };

  const handleClearSettings = () => {
    localStorage.removeItem(SETTINGS_KEY);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(DEFAULT_SETTINGS));

    setApiKey(DEFAULT_SETTINGS.apiKey);
    setModel(DEFAULT_SETTINGS.model);
    setMaxLength(DEFAULT_SETTINGS.maxLength);
    setTemperature(DEFAULT_SETTINGS.temperature);
    setTopP(DEFAULT_SETTINGS.topP);
    setPresencePenalty(DEFAULT_SETTINGS.presencePenalty);
    setFrequencyPenalty(DEFAULT_SETTINGS.frequencyPenalty);
    setReplacements([]);
    setModelsList(fallbackModels);

    prevApiKey.current = DEFAULT_SETTINGS.apiKey;

    showToast('✅ تنظیمات به حالت کارخانه بازنشانی شد.', 'success');
    setConfirmDeleteShow(false);
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>تنظیمات Groq LLM</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={tabKey} onSelect={(k) => setTabKey(k)} className="mb-3">
            <Tab eventKey="general" title="تنظیمات عمومی">
              <GeneralSettings
                apiKey={apiKey}
                setApiKey={setApiKey}
                model={model}
                setModel={setModel}
                modelsList={modelsList}
                loadingModels={loadingModels}
                onApiKeyChange={handleApiKeyChange}
              />
            </Tab>
            <Tab eventKey="advanced" title="تنظیمات پیشرفته">
              <AdvancedSettings
                maxLength={maxLength}
                setMaxLength={setMaxLength}
                temperature={temperature}
                setTemperature={setTemperature}
                topP={topP}
                setTopP={setTopP}
                presencePenalty={presencePenalty}
                setPresencePenalty={setPresencePenalty}
                frequencyPenalty={frequencyPenalty}
                setFrequencyPenalty={setFrequencyPenalty}
              />
            </Tab>
            <Tab eventKey="replace" title="جایگزینی کلمات">
              <ReplaceSettings
                replacements={replacements}
                setReplacements={setReplacements}
              />
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="danger" onClick={confirmClearSettings}>
            حذف همه تنظیمات
          </Button>
          <div>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              بستن
            </Button>
            <Button variant="primary" onClick={handleSave}>
              ذخیره
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={confirmDeleteShow}
        handleConfirm={handleClearSettings}
        handleCancel={() => setConfirmDeleteShow(false)}
        title="حذف همه تنظیمات"
        message="آیا مطمئن هستید که می‌خواهید همه تنظیمات ذخیره شده را پاک کنید؟"
      />

      <ToastMessage
        show={toastShow}
        onClose={() => setToastShow(false)}
        variant={toastVariant}
        content={toastContent}
      />
    </>
  );
}

export default SettingsModal;
